import { AxiosError, AxiosRequestConfig } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';

import { HttpRequest } from 'http';
import api from '../../config/api/api';

import { useLoadingGroup } from '../LoadingGroup/useLoadingGroup';
import { Exception, UseRequest } from '../models';
import { util } from '../utils/util';

const useRequest = <S extends any = any, E extends Exception = Exception>(
  r?: AxiosRequestConfig & { globalLoading?: boolean }
): UseRequest<S, E> => {
  const loadingGroup = useLoadingGroup();
  const id = useRef(Math.random().toString(36).substr(2, 9));
  const [requestConfig, setRequestConfig] = useState<
    AxiosRequestConfig & { globalLoading?: boolean }
  >();
  const [status, setStatus] = useState<HttpRequest<S, E>>({
    backEndErrors: null,
    error: false,
    loading: false,
    success: false,
    successData: null,
    statusCode: 0,
  });
  const isActive = useRef<boolean>(false);
  const abortSignal = useRef<AbortController>(new AbortController());

  useMemo(() => {
    if (r !== undefined) {
      setRequestConfig(r);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(r)]);

  const reset = () => {
    setStatus({
      backEndErrors: null,
      error: false,
      loading: false,
      success: false,
      successData: null,
      statusCode: 0,
    });
  };

  const fetch = (r2?: AxiosRequestConfig & { globalLoading?: boolean }) => {
    const isGlobal = r2?.globalLoading ?? requestConfig?.globalLoading ?? false;

    if (isGlobal) {
      loadingGroup.setLoading(id.current, true);
    }

    setStatus({
      backEndErrors: null,
      error: false,
      loading: true,
      success: false,
      successData: null,
      statusCode: 0,
    });
    const f = async () => {
      isActive.current = true;
      if (r2 !== undefined) {
        try {
          const response = await api.request({ ...r2, signal: abortSignal.current.signal });
          const data = await response?.data;
          setStatus({
            backEndErrors: null,
            error: false,
            loading: false,
            success: true,
            successData: data,
            statusCode: response.status,
          });
        } catch (er) {
          const axiosError = (await er) as AxiosError;
          const errorResponse = axiosError.response;

          const responseType = errorResponse?.config.responseType;

          if (axiosError.response?.status === 401) {
            throw axiosError;
          }

          if (responseType === 'blob') {
            const jsonString = await errorResponse?.data.text();
            const data = JSON.parse(jsonString);

            const backendError: Exception = {
              status: data?.status ?? null,
              data: data?.data ?? null,
              message: data?.message ?? data.title ?? null,
              source: data?.source ?? null,
              stackTrace: data?.stackTrace ?? null,
            };
            setStatus({
              backEndErrors: backendError as E,
              error: true,
              loading: false,
              success: false,
              successData: null,
              statusCode: 0,
            });
          } else {
            if (axiosError.response?.status === 413) {
              const backendError: Exception = {
                status: errorResponse?.status ?? null,
                data: errorResponse?.data?.data ?? null,
                message: 'Arquivo muito grande',
                source: errorResponse?.data?.source ?? null,
                stackTrace: errorResponse?.data?.stackTrace ?? null,
              };
              setStatus({
                backEndErrors: backendError as E,
                error: true,
                loading: false,
                success: false,
                successData: null,
                statusCode: 0,
              });
              return;
            }
            const backendError: Exception = {
              status: errorResponse?.status ?? null,
              data: errorResponse?.data?.data ?? null,
              message: errorResponse?.data?.message ?? null,
              source: errorResponse?.data?.source ?? null,
              stackTrace: errorResponse?.data?.stackTrace ?? null,
            };
            setStatus({
              backEndErrors: backendError as E,
              error: true,
              loading: false,
              success: false,
              successData: null,
              statusCode: 0,
            });
          }

          // eslint-disable-next-line no-console
          console.error(errorResponse);
        } finally {
          if (isGlobal) {
            loadingGroup.setLoading(id.current, false);
          }
          isActive.current = false;
        }
      }
    };

    if (!isActive.current) {
      f();
    }
  };

  const useFetchBy = (condition: any, trigger?: any | 'init') => {
    const f = requestConfig ? () => fetch(requestConfig) : fetch;
    const dependencyListEmptyWhenTriggerEqualArray =
      trigger === 'init' ? [] : [trigger ?? condition];
    useEffect(() => {
      if (condition) f();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencyListEmptyWhenTriggerEqualArray);
  };

  const useFetchMemoBy = (condition: any, trigger?: any | 'init') => {
    const f = requestConfig ? () => fetch(requestConfig) : fetch;
    const dependencyListEmptyWhenTriggerEqualArray =
      trigger === 'init' ? [] : [trigger ?? condition];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(() => {
      if (condition) f();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencyListEmptyWhenTriggerEqualArray);
  };

  const useSuccess = (f: () => void) => {
    useEffect(() => {
      if (status.success) {
        f();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(status)]);
  };

  const useFailure = (f: () => void) => {
    useEffect(() => {
      if (status.error) {
        f();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(status)]);
  };

  const useLoading = (f: () => void) => {
    useEffect(() => {
      if (status.loading) {
        f();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(status)]);
  };

  const useChange = (f: () => void) => {
    useEffect(() => {
      f();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(status)]);
  };

  const useRequestConfig = (config: AxiosRequestConfig<any> | undefined) => {
    useMemo(() => {
      if (!util.isEqualDeep(config, requestConfig)) setRequestConfig(config);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);
  };

  return {
    fetch: requestConfig ? () => fetch(requestConfig) : fetch,
    useChange,
    abortSignal: abortSignal.current,
    ...status,
    useFetchMemoBy,
    reset,
    useLoading,
    useFailure,
    setRequestConfig,
    requestConfig,
    useFetchBy,
    useSuccess,
    useRequestConfig,
    setStatus,
  };
};

export default useRequest;
